import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/base/Seo";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import BackToTop from "@/components/base/BackToTop";
import { getLocations } from "@/services/publicApi";
import LocationsSection from "@/pages/home/components/LocationsSection";
import { SITE } from "@/config/site";
import { taluksForDistrict } from "@/config/regions";
import type { Location } from "@/types";

export default function Locations() {
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getLocations()
      .then(setLocations)
      .catch(() => setLocations([]))
      .finally(() => setLoading(false));
  }, []);

  const total = locations.reduce((sum, l) => sum + l.businessCount, 0);

  return (
    <>
      <Seo
        title="Locations We Cover — Mysore, Mandya, Chamarajanagar & Kodagu"
        description="Explore local businesses district by district across the Mysore region, with locality-level listings for Mysore, Mandya, Chamarajanagar and Kodagu (Coorg)."
        keywords="Mysore locations, Mandya businesses, Chamarajanagar directory, Kodagu Coorg listings"
        path="/locations"
      />

      <div className="border-b border-background-200 bg-background-100/70">
        <div className="mx-auto max-w-[1400px] px-4 md:px-6 py-8 md:py-10">
          <Breadcrumbs items={[{ label: "Locations" }]} />
          <h1 className="mt-3 font-heading text-2xl md:text-4xl font-extrabold tracking-tight text-foreground-950">
            District coverage
          </h1>
          <p className="mt-2 max-w-2xl text-sm md:text-base text-foreground-600">
            We cover {locations.length} districts and {total.toLocaleString()}+ listings across the Mysore region.
            Pick your district to see local businesses nearby.
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-4 md:px-6 py-10 md:py-14">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-72 animate-pulse rounded-lg border border-background-200 bg-background-100/60"></div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {locations.map((loc) => (
              <Link
                key={loc.id}
                to={`/location/${loc.slug}`}
                className="group relative block h-72 overflow-hidden rounded-lg cursor-pointer"
              >
                <img
                  src={loc.image}
                  alt={`Businesses in ${loc.name}`}
                  title={`${loc.name} business directory`}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/40 to-transparent"></div>
                <div className="absolute inset-x-0 bottom-0 p-5 md:p-6">
                  <span className="inline-flex items-center gap-1 rounded-full bg-primary-500 px-3 py-1 text-[11px] font-bold text-foreground-950">
                    <i className="ri-map-pin-2-fill"></i> {loc.district} district
                  </span>
                  <h2 className="mt-3 font-heading text-xl md:text-2xl font-extrabold text-background-50">{loc.name}</h2>
                  <p className="mt-1.5 max-w-md text-sm text-background-200 leading-relaxed">{loc.description}</p>
                  <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-primary-300">
                    {loc.businessCount.toLocaleString()} listings
                    <i className="ri-arrow-right-line"></i>
                  </span>
                  {taluksForDistrict(loc.district).length > 0 && (
                    <span className="mt-1.5 inline-flex items-center gap-1 text-xs text-background-200">
                      <i className="ri-map-2-line"></i> {taluksForDistrict(loc.district).length} taluks inside
                    </span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}

        <div className="mt-14 md:mt-16">
          <LocationsSection locations={locations} showAllLink={false} embedded />
        </div>

        <div className="mt-12 rounded-lg border border-background-200 bg-background-100/60 p-6 md:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
          <div>
            <h2 className="font-heading text-lg font-bold text-foreground-950">Not seeing your town?</h2>
            <p className="mt-1.5 text-sm text-foreground-600 max-w-xl">
              We're expanding across Karnataka. Tell us where you'd like coverage next, or list your business
              in {SITE.regions.join(", ")}.
            </p>
          </div>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-foreground-950 hover:bg-primary-600 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-mail-send-line"></i> Suggest a location
          </Link>
        </div>
      </div>

      <BackToTop showAfter={600} />
    </>
  );
}